import { useEffect, useRef } from "react";

/**
 * useMagnetic
 *
 * Subtle "magnetic" pull: the element drifts toward the cursor when the
 * cursor is near it. Great for primary CTAs — adds that Framer feel.
 *
 * Writes transform directly on the ref'd element so it composes cleanly
 * with existing hover states. Uses rAF for smooth tracking.
 *
 * Disabled for prefers-reduced-motion and coarse (touch) pointers.
 *
 * Props:
 *   - radius: pixels around the element where magnetism activates (default 120)
 *   - strength: pull distance ratio 0..1 (default 0.25 = 25% of the cursor-to-centre delta)
 */
export default function useMagnetic({ radius = 120, strength = 0.25 } = {}) {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof window === "undefined") return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const coarsePointer =
      window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    if (prefersReduced || coarsePointer) return;

    let ticking = false;
    let lastEvent = null;

    const update = () => {
      ticking = false;
      if (!lastEvent) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = lastEvent.clientX - cx;
      const dy = lastEvent.clientY - cy;
      const dist = Math.hypot(dx, dy);
      const effectiveRadius = Math.max(rect.width, rect.height) / 2 + radius;
      if (dist < effectiveRadius) {
        const falloff = 1 - dist / effectiveRadius;
        const tx = dx * strength * falloff;
        const ty = dy * strength * falloff;
        el.style.setProperty("--mag-x", `${tx.toFixed(2)}px`);
        el.style.setProperty("--mag-y", `${ty.toFixed(2)}px`);
      } else {
        el.style.setProperty("--mag-x", "0px");
        el.style.setProperty("--mag-y", "0px");
      }
    };

    const onMove = (e) => {
      lastEvent = e;
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    const onLeave = () => {
      el.style.setProperty("--mag-x", "0px");
      el.style.setProperty("--mag-y", "0px");
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("mouseleave", onLeave, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
    };
  }, [radius, strength]);

  return ref;
}
